import { useState, useEffect, useRef, useImperativeHandle, forwardRef } from "react";
import { MoreHorizontal, X, Eye, EyeOff, Home, Maximize2, Minimize2, RefreshCw } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Default ticker settings
const DEFAULT_SETTINGS = {
  enabled: true,
  showOnAllPages: false, // Home only by default
  size: "normal", // 'compact', 'normal', 'large'
  speed: "normal", // 'slow', 'normal', 'fast'
};

// Load settings from localStorage
const loadSettings = () => {
  try {
    const saved = localStorage.getItem("parseeker_ticker_settings");
    return saved ? { ...DEFAULT_SETTINGS, ...JSON.parse(saved) } : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
};

// Save settings to localStorage
const saveSettings = (settings) => {
  try {
    localStorage.setItem("parseeker_ticker_settings", JSON.stringify(settings));
  } catch {
    console.error("Failed to save ticker settings");
  }
};

// Static fallback items for immediate display
const INITIAL_TICKER_ITEMS = [
  "⛳ Welcome to Par Seeker - Your Golf Command Center",
  "🏌️ Track scores, calculate handicaps, challenge buddies",
  "📊 Loading live golf news...",
];

const GolfNewsTicker = forwardRef(({ currentPath = "/home", externalShowSettings = false, onSettingsClose }, ref) => {
  const [settings, setSettings] = useState(() => {
    // Load and immediately "touch" settings to ensure animation works
    const loaded = loadSettings();
    // Force a save to ensure localStorage is initialized
    saveSettings(loaded);
    return loaded;
  });
  const [showMenu, setShowMenu] = useState(false);
  const [tickerItems, setTickerItems] = useState(INITIAL_TICKER_ITEMS);
  const [loading, setLoading] = useState(true);
  const [animationKey, setAnimationKey] = useState(0); // Force re-render of animation
  const [lastUpdated, setLastUpdated] = useState(null);
  const tickerRef = useRef(null);
  const menuRef = useRef(null);

  // Expose openSettings method via ref
  useImperativeHandle(ref, () => ({
    openSettings: () => setShowMenu(true),
    closeSettings: () => setShowMenu(false),
  }));

  // Handle external settings trigger
  useEffect(() => {
    if (externalShowSettings) {
      setShowMenu(true);
    }
  }, [externalShowSettings]);

  // Call onSettingsClose when menu closes
  useEffect(() => {
    if (!showMenu && onSettingsClose) {
      onSettingsClose();
    }
  }, [showMenu, onSettingsClose]);

  // Fetch ticker news
  const fetchTickerNews = async (forceRefresh = false) => {
    try {
      const url = forceRefresh 
        ? `${API}/golf-news/ticker?force_refresh=true`
        : `${API}/golf-news/ticker`;
      const response = await axios.get(url);
      if (response.data.success && response.data.items && response.data.items.length > 0) {
        setTickerItems(response.data.items);
        setLastUpdated(new Date());
        // Restart animation with new content
        setAnimationKey(prev => prev + 1);
      }
    } catch (error) {
      console.error("Error fetching ticker news:", error);
      // Keep using current items or fallback
      if (tickerItems.length === 0) {
        setTickerItems([
          "⛳ Welcome to Par Seeker - Track your golf journey!",
          "🏌️ Log your scores and watch your handicap improve",
          "📊 Par Seeker tracks your product handicap estimate automatically",
          "🏆 Challenge your buddies to skins games",
        ]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Force animation restart on mount
  useEffect(() => {
    // Trigger animation restart after component mounts
    const timer = setTimeout(() => {
      setAnimationKey(1);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Fetch on mount and every 15 minutes
  useEffect(() => {
    fetchTickerNews();
    const interval = setInterval(fetchTickerNews, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update settings and persist
  const updateSettings = (key, value) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    saveSettings(newSettings);
  };

  // Check if ticker should be visible
  const shouldShow = () => {
    if (!settings.enabled) return false;
    if (settings.showOnAllPages) return true;
    // Home only mode - check if on home/dashboard path
    return currentPath === "/home" || currentPath === "/dashboard" || currentPath === "/";
  };

  if (!shouldShow()) return null;

  // Size styles
  const sizeStyles = {
    compact: "h-7 text-xs",
    normal: "h-9 text-sm",
    large: "h-11 text-base",
  };

  // Speed styles (animation duration)
  const speedDurations = {
    slow: "60s",
    normal: "40s",
    fast: "25s",
  };

  // Create the ticker content - duplicate for seamless loop
  const tickerContent = [...tickerItems, ...tickerItems].join("  •  ");

  return (
    <div 
      className={`golf-ticker ${sizeStyles[settings.size]} relative z-40`}
      style={{
        background: "linear-gradient(90deg, var(--ps-bg-base) 0%, var(--ps-bg-elevated) 50%, var(--ps-bg-base) 100%)",
        borderTop: "2px solid var(--ps-primary)",
        borderBottom: "2px solid var(--ps-primary)",
      }}
    >
      {/* Ticker Content */}
      <div className="ticker-container overflow-hidden flex-1">
        <div 
          key={`ticker-${animationKey}`}
          ref={tickerRef}
          className="ticker-content whitespace-nowrap ticker-animate"
          style={{
            animation: `ticker-scroll ${speedDurations[settings.speed]} linear infinite`,
            color: "var(--ps-primary)",
            fontFamily: "'Roboto Mono', 'Courier New', monospace",
            fontWeight: 600,
            letterSpacing: "0.5px",
            textShadow: "0 0 8px rgba(34, 197, 94, 0.3)",
          }}
        >
          {tickerContent}
        </div>
      </div>

      {/* Settings Menu Button - More prominent */}
      <div className="relative" ref={menuRef}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          className="ticker-menu-btn px-4 h-full flex items-center justify-center bg-[var(--ps-primary)]/10 hover:bg-[var(--ps-primary)]/20 transition-colors border-l-2 border-[var(--ps-primary)]"
          data-testid="ticker-settings-btn"
          aria-label="Ticker settings"
        >
          <MoreHorizontal className="w-5 h-5 text-[var(--ps-primary)]" />
        </button>

        {/* Dropdown Menu */}
        {showMenu && (
          <div 
            className="ticker-menu absolute bottom-full right-0 mb-2 w-56 rounded-xl overflow-hidden shadow-xl"
            style={{
              background: "var(--ps-bg-surface)",
              border: "1px solid var(--ps-border)",
            }}
          >
            <div className="p-3 border-b border-[var(--ps-border)]">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-[var(--ps-text-primary)]">News Ticker</span>
                <button onClick={() => setShowMenu(false)} className="p-1 hover:bg-[var(--ps-bg-hover)] rounded">
                  <X className="w-4 h-4 text-[var(--ps-text-muted)]" />
                </button>
              </div>
              {lastUpdated && (
                <p className="text-xs text-[var(--ps-text-muted)] mt-1">
                  Updated {lastUpdated.toLocaleTimeString()}
                </p>
              )}
            </div>

            <div className="p-2 space-y-1">
              {/* Visibility Toggle */}
              <button
                onClick={() => updateSettings("enabled", !settings.enabled)}
                className="ticker-menu-item w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--ps-bg-hover)] transition-colors"
              >
                {settings.enabled ? (
                  <Eye className="w-4 h-4 text-[var(--ps-primary)]" />
                ) : (
                  <EyeOff className="w-4 h-4 text-[var(--ps-text-muted)]" />
                )}
                <span className="text-sm text-[var(--ps-text-primary)]">
                  {settings.enabled ? "Hide Ticker" : "Show Ticker"}
                </span>
              </button>

              {/* Page Visibility */}
              <button
                onClick={() => updateSettings("showOnAllPages", !settings.showOnAllPages)}
                className="ticker-menu-item w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--ps-bg-hover)] transition-colors"
              >
                <Home className="w-4 h-4 text-[var(--ps-text-secondary)]" />
                <span className="text-sm text-[var(--ps-text-primary)]">
                  {settings.showOnAllPages ? "Home Only" : "Show Everywhere"}
                </span>
              </button>

              {/* Divider */}
              <div className="h-px bg-[var(--ps-border)] my-1" />

              {/* Size Options */}
              <div className="px-3 py-2">
                <span className="text-xs font-medium text-[var(--ps-text-muted)] uppercase tracking-wide">Size</span>
                <div className="flex gap-1 mt-2">
                  {["compact", "normal", "large"].map((size) => (
                    <button
                      key={size}
                      onClick={() => updateSettings("size", size)}
                      className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        settings.size === size
                          ? "bg-[var(--ps-primary)] text-white"
                          : "bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]"
                      }`}
                    >
                      {size.charAt(0).toUpperCase() + size.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Speed Options */}
              <div className="px-3 py-2">
                <span className="text-xs font-medium text-[var(--ps-text-muted)] uppercase tracking-wide">Speed</span>
                <div className="flex gap-1 mt-2">
                  {["slow", "normal", "fast"].map((speed) => (
                    <button
                      key={speed}
                      onClick={() => updateSettings("speed", speed)}
                      className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        settings.speed === speed
                          ? "bg-[var(--ps-primary)] text-white"
                          : "bg-[var(--ps-bg-elevated)] text-[var(--ps-text-secondary)] hover:bg-[var(--ps-bg-hover)]"
                      }`}
                    >
                      {speed.charAt(0).toUpperCase() + speed.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-[var(--ps-border)] my-1" />

              {/* Refresh Button */}
              <button
                onClick={() => {
                  setLoading(true);
                  fetchTickerNews(true); // Force refresh from RSS feeds
                  setShowMenu(false);
                }}
                className="ticker-menu-item w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[var(--ps-bg-hover)] transition-colors"
              >
                <RefreshCw className={`w-4 h-4 text-[var(--ps-text-secondary)] ${loading ? 'animate-spin' : ''}`} />
                <span className="text-sm text-[var(--ps-text-primary)]">Refresh News</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CSS for ticker animation */}
      <style>{`
        .golf-ticker {
          display: flex;
          align-items: center;
        }
        
        .ticker-container {
          mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
          -webkit-mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent);
        }
        
        @keyframes ticker-scroll {
          0% {
            transform: translate3d(0, 0, 0);
          }
          100% {
            transform: translate3d(-50%, 0, 0);
          }
        }
        
        .ticker-content {
          display: inline-block;
          padding-left: 100%;
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        
        .ticker-content.ticker-animate {
          animation-timing-function: linear;
          animation-fill-mode: forwards;
        }
        
        .ticker-content:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
});

GolfNewsTicker.displayName = 'GolfNewsTicker';

export default GolfNewsTicker;
